import { HTMLAttributes, useEffect, useRef } from 'react';
import { usePreservedCallback } from '@toss/react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { match } from 'ts-pattern';
import { ZepApiError, ZepApiStatusType } from '@zep/apis';
import { useDialog } from '@zep/dialog';
import { cn } from '@zep/utils';
import { useAuth } from '../../hooks';
import { useVerifyEmail } from '../../hooks/useVerifyEmail';
import { goAgreeTermsPage } from '../../utils';
import { LoginPrivacyPolicy } from '../common/loginPrivacyPolicy';
import { EmailVerifyCodeInput } from './EmailVerifyCodeInput';
import { OTPMethods } from './OtpInput';
interface EmailLoginSectionProps {
  redirectUrl: string;
  email: string;
  initialCode?: string;
}
const LoginEmail = ({
  redirectUrl,
  email,
  initialCode = ''
}: EmailLoginSectionProps) => {
  const {
    t
  } = useTranslation(['common']);
  const otpMethod = useRef<OTPMethods>(null);
  const {
    alert
  } = useDialog();
  const {
    code,
    setCode,
    errorMessage,
    setErrorMessage,
    verifyCode,
    isLoading
  } = useVerifyEmail(email, initialCode);
  const {
    login
  } = useAuth();
  const router = useRouter();
  const handleVerifyCode = usePreservedCallback(async () => {
    try {
      const [, user] = await verifyCode();
      await login(user);
      if (redirectUrl.startsWith('https')) {
        window.location.href = redirectUrl;
      } else {
        router.push(redirectUrl);
      }
    } catch (e) {
      console.error(e);
      if (e instanceof ZepApiError) {
        match(e.status).with(ZepApiStatusType.AUTH_CONFIRM_CODE_INVALID, () => {
          setErrorMessage(t('common:authCode.error.wrongCode'));
          // otpMethod.current?.focus(0);
        }).with(ZepApiStatusType.TOO_MANY_SIGNIN_TRIAL, () => {
          alert({
            title: '',
            confirmText: t('common:confirm'),
            content: t('common:authCode.error.exceeded'),
            confirmAction: async () => {
              // await props.onAttemptsExceeded?.();
            }
          });
        }).with(ZepApiStatusType.AUTH_CONFIRM_EXPIRED, () => {
          // 인증 시간이 만료되었습니다.
          setErrorMessage(t('common:error.api.unknown'));
        }).with(ZepApiStatusType.NOT_FOUND_PLAYER, () => {
          // 존재하지 않는 이메일 / 멤버를 찾을 수 없습니다.
          setErrorMessage(t('common:error.api.unknown'));
        }).with(ZepApiStatusType.NOT_AGREED_TERMS_OR_PRIVACY_POLICY, () => {
          goAgreeTermsPage((e as ZepApiError).bindingData?.token ?? '', redirectUrl);
        }).with(ZepApiStatusType.TOO_MANY_AUTH_CODE_FAILURES, () => {
          alert({
            title: '',
            confirmText: t('common:confirm'),
            content: t('common:authCode.error.wrong_code_exceed')
          });
        }).otherwise(() => {
          setErrorMessage(t('common:error.api.unknown'));
        });
      } else {
        setErrorMessage(t('common:error.api.unknown'));
      }
      setCode('');
      setTimeout(() => {
        otpMethod.current?.focus(0);
      }, 0);
    }
  });
  useEffect(() => {
    if (code.length === 6) {
      handleVerifyCode();
    }
  }, [code, handleVerifyCode]);
  return <LoginEmailWrapper data-sentry-element="LoginEmailWrapper" data-sentry-component="LoginEmail" data-sentry-source-file="LoginEmail.tsx">
      <EmailLoginTitle data-sentry-element="EmailLoginTitle" data-sentry-source-file="LoginEmail.tsx">{t('common:authCode.title')}</EmailLoginTitle>
      <EmailLoginDescription className="mt-[16px]" data-sentry-element="EmailLoginDescription" data-sentry-source-file="LoginEmail.tsx">
        {t('common:authCode.description')}
      </EmailLoginDescription>
      <EmailVerifyCodeInput className="mt-[24px]" ref={otpMethod} value={code} onChange={setCode} disabled={isLoading} data-sentry-element="EmailVerifyCodeInput" data-sentry-source-file="LoginEmail.tsx" />
      {errorMessage && <EmailLoginErrorMessage className="mt-[10px]">
          {errorMessage}
        </EmailLoginErrorMessage>}
      <LoginPrivacyPolicy className="mt-[24px]" data-sentry-element="LoginPrivacyPolicy" data-sentry-source-file="LoginEmail.tsx" />
    </LoginEmailWrapper>;
};
const EmailLoginTitle = ({
  className,
  children,
  ...rest
}: HTMLAttributes<HTMLSpanElement>) => {
  return <span className={cn('text-h3', className)} {...rest} data-sentry-component="EmailLoginTitle" data-sentry-source-file="LoginEmail.tsx">
      {children}
    </span>;
};
const EmailLoginDescription = ({
  className,
  children,
  ...rest
}: HTMLAttributes<HTMLDivElement>) => {
  return <div className={cn('text-center text-[13px] font-medium whitespace-pre-wrap', className)} {...rest} data-sentry-component="EmailLoginDescription" data-sentry-source-file="LoginEmail.tsx">
      {children}
    </div>;
};
const EmailLoginErrorMessage = ({
  className,
  children,
  ...rest
}: HTMLAttributes<HTMLSpanElement>) => {
  return <span className={cn('text-center text-red m-0 text-[13px] font-medium', className)} {...rest} data-sentry-component="EmailLoginErrorMessage" data-sentry-source-file="LoginEmail.tsx">
      {children}
    </span>;
};
const LoginEmailWrapper = (props: HTMLAttributes<HTMLDivElement>) => {
  const {
    children,
    className
  } = props;
  return <div className={cn('flex flex-col items-center', className)} data-sentry-component="LoginEmailWrapper" data-sentry-source-file="LoginEmail.tsx">
      {children}
    </div>;
};
export { LoginEmail };